import { TSavedLaneSchema } from 'transfix-form/forms/SavedLaneForm/schema'
import { Maybe } from 'types/graphqlTypes'
import {
  BookableShipmentSavedSearch,
  CreateBookableShipmentSavedSearchRequestBody,
  useCreateBookableShipmentSavedSearch,
  useDeleteBookableShipmentSavedSearch,
  useUpdateBookableShipmentSavedSearch,
} from 'apps/fleet-planner/types'
import { getBookableShipmentSavedSearchRequestBody } from '../components/saved-lanes/utils/getBookableShipmentSavedSearchRequestBody'

export interface IUseSavedLaneMutationsState {
  loading: boolean
  deleteSavedLane: (savedLaneId: number) => Promise<boolean>
  createSavedLane: (
    savedLaneFormData: TSavedLaneSchema
  ) => Promise<Maybe<BookableShipmentSavedSearch>>
  createSavedLaneFromSearch: (
    searchFilters: BookableShipmentSavedSearch,
    motorCarrierId: number
  ) => Promise<Maybe<BookableShipmentSavedSearch>>
  updateSavedLane: (
    savedLaneFormData: TSavedLaneSchema
  ) => Promise<Maybe<BookableShipmentSavedSearch>>
  updateSavedLaneNotificationSetting: (
    enableNotifications: boolean
  ) => Promise<Maybe<BookableShipmentSavedSearch>>
}

export interface IUseSavedLaneMutationsProps {
  savedLaneId?: Maybe<number>
}

export const useSavedLaneMutations = ({
  savedLaneId,
}: IUseSavedLaneMutationsProps = {}): IUseSavedLaneMutationsState => {
  /**
   * Restful Mutations
   */
  const {
    loading: createLoading,
    mutate: createSavedLaneMutation,
  } = useCreateBookableShipmentSavedSearch({})
  const {
    loading: deleteLoading,
    mutate: deleteSavedLaneMutation,
  } = useDeleteBookableShipmentSavedSearch({})
  const {
    loading: updateLoading,
    mutate: updateSavedLaneMutation,
  } = useUpdateBookableShipmentSavedSearch({ id: savedLaneId || 0 })

  const createSavedLane = async (savedLaneFormData: TSavedLaneSchema) => {
    try {
      const { bookableShipmentSavedSearch } = await createSavedLaneMutation(
        getBookableShipmentSavedSearchRequestBody(savedLaneFormData)
      )
      return bookableShipmentSavedSearch
    } catch {
      return null
    }
  }

  const createSavedLaneFromSearch = async (
    searchFilters: BookableShipmentSavedSearch,
    motorCarrierId: number
  ) => {
    try {
      const requestBody: CreateBookableShipmentSavedSearchRequestBody = {
        bookableShipmentSavedSearch: {
          ...searchFilters,
          motorCarrierId,
          hasNotificationsEnabled: true,
        },
      }
      const { bookableShipmentSavedSearch } = await createSavedLaneMutation(requestBody)
      return bookableShipmentSavedSearch
    } catch {
      return null
    }
  }

  const deleteSavedLane = async (savedLaneId: number) => {
    try {
      await deleteSavedLaneMutation(savedLaneId)
      return true
    } catch {
      return false
    }
  }

  const updateSavedLane = async (savedLaneFormData: TSavedLaneSchema) => {
    try {
      const { bookableShipmentSavedSearch } = await updateSavedLaneMutation(
        getBookableShipmentSavedSearchRequestBody(savedLaneFormData)
      )
      return bookableShipmentSavedSearch
    } catch {
      return null
    }
  }

  const updateSavedLaneNotificationSetting = async (enableNotifications: boolean) => {
    try {
      const { bookableShipmentSavedSearch } = await updateSavedLaneMutation({
        bookableShipmentSavedSearch: {
          hasNotificationsEnabled: enableNotifications,
        },
      })
      return bookableShipmentSavedSearch
    } catch {
      return null
    }
  }

  return {
    loading: deleteLoading || createLoading || updateLoading,
    deleteSavedLane,
    createSavedLane,
    createSavedLaneFromSearch,
    updateSavedLane,
    updateSavedLaneNotificationSetting,
  }
}
